const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Formats a "YYYY-MM-DD" (or ISO) date as numeric `d/M/yyyy`, e.g. "5/8/2026". */
export function formatDateLabel(date?: string): string {
  if (!date) return '—';

  const [year, month, day] = String(date).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '—';

  return `${day}/${month}/${year}`;
}

/** Returns the short weekday name for a date, e.g. "Wed". */
export function formatWeekday(date?: string): string {
  if (!date) return '';

  const [year, month, day] = String(date).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '';

  return WEEKDAYS[new Date(year, month - 1, day).getDay()];
}

/** Formats a date with its weekday name as e.g. "Wed, 5/8/2026". */
export function formatDateWithWeekday(date?: string): string {
  const dateLabel = formatDateLabel(date);
  const weekday = formatWeekday(date);

  return weekday ? `${weekday}, ${dateLabel}` : dateLabel;
}

/** Formats an "HH:mm" time as e.g. "09:26 pm". */
export function formatTimeLabel(time?: string): string {
  if (!time) return '';

  const [hours, minutes] = time.split(':').map(Number);
  if (Number.isNaN(hours)) return '';

  const period = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(hour12).padStart(2, '0')}:${String(minutes ?? 0).padStart(2, '0')} ${period}`;
}

/** Formats a start/end time range as e.g. "09:26 pm – 09:56 pm". */
export function formatTimeRange(start?: string, end?: string): string {
  const startLabel = formatTimeLabel(start);
  const endLabel = formatTimeLabel(end);

  if (!startLabel) return '';
  return endLabel ? `${startLabel} – ${endLabel}` : startLabel;
}

/** Formats an ISO date/datetime as numeric `d/M/yyyy`, e.g. "1/1/2026". */
export function formatOrdinalDate(date?: string): string {
  if (!date) return '—';

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';

  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

/** Formats an ISO datetime as e.g. "1/1/2026, 02:05 pm". */
export function formatOrdinalDateTime(date?: string): string {
  if (!date) return '—';

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';

  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${formatOrdinalDate(date)}, ${formatTimeLabel(time)}`;
}

/** Kept for backwards compatibility — now returns the numeric `d/M/yyyy`. */
export function formatOrdinalDateLong(date?: string): string {
  return formatOrdinalDate(date);
}

/** Formats an ISO datetime as numeric `d/M/yyyy, hh:mm am/pm`, e.g. "1/4/2026, 02:05 pm". */
export function formatOrdinalDateTimeLong(date?: string): string {
  return formatOrdinalDateTime(date);
}

/**
 * Formats an ISO datetime in a specific IANA timezone as numeric
 * `d/M/yyyy, hh:mm am/pm`, e.g. "1/1/2026, 02:05 pm" for the given zone.
 * Falls back to the browser's local timezone when no zone is supplied. Used by
 * reports so the on-screen timestamps match the exported PDF/Excel/CSV files
 * (which are rendered in the hospital timezone).
 */
export function formatOrdinalDateTimeInTimeZone(date?: string, timeZone?: string): string {
  if (!date) return '—';

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  if (!timeZone) return formatOrdinalDateTime(date);

  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(d);
  } catch {
    // Invalid timezone — fall back to local formatting.
    return formatOrdinalDateTime(date);
  }

  const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  const day = Number(pick('day'));
  const month = Number(pick('month'));
  const year = pick('year');
  const hour = pick('hour');
  const minute = pick('minute');
  const dayPeriod = pick('dayPeriod').toLowerCase();

  if (!day || !month || !year) return formatOrdinalDateTime(date);

  return `${day}/${month}/${year}, ${hour}:${minute} ${dayPeriod}`;
}
